import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { describeError } from '../../lib/api.ts';
import { api } from '../../lib/client.ts';
import { useVisibleDefinitions } from '../../lib/custom-fields.ts';
import { useSession } from '../../lib/session.tsx';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '../ui/sheet.tsx';
import { CandidateDetail } from './candidate-detail.tsx';
import { CandidateForm } from './candidate-form.tsx';

/**
 * The candidate side panel the Candidates list opens (RELAY-944), for pages that are not that
 * list: an overlay over whatever is open (a project's shortlist), so closing it leaves the page
 * exactly as it was. Edit opens the candidate form on top; "Open full page" leaves for
 * `/candidates/$id`. `candidateId` null = closed.
 */
export function CandidateDrawer({
  candidateId,
  onClose,
  onChanged,
}: {
  candidateId: string | null;
  onClose: () => void;
  /** After an edit or an upload here: what the page shows of the candidate may be stale. */
  onChanged?: () => void | Promise<void>;
}) {
  const qc = useQueryClient();
  const session = useSession();
  const definitions = useVisibleDefinitions('candidates');
  const [editing, setEditing] = useState(false);
  const candidate = useQuery({
    queryKey: ['candidate', candidateId],
    queryFn: () => api.candidates.get(candidateId!),
    enabled: candidateId !== null,
  });
  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ['candidate', candidateId] });
    await onChanged?.();
  };
  return (
    <>
      <Sheet open={candidateId !== null} onOpenChange={(o) => !o && onClose()}>
        {candidateId !== null && (
          <SheetContent data-testid="candidate-panel">
            <SheetTitle className="sr-only">Candidate</SheetTitle>
            <SheetDescription className="sr-only">
              Details of the selected candidate.
            </SheetDescription>
            {candidate.data ? (
              <CandidateDetail
                candidate={candidate.data}
                definitions={definitions}
                fullPageHref={`/candidates/${candidateId}`}
                onEdit={() => setEditing(true)}
                onChanged={refresh}
              />
            ) : candidate.error != null ? (
              <p className="text-danger text-sm" role="alert">
                {describeError(candidate.error)}
              </p>
            ) : (
              <p className="text-muted-foreground text-sm">Loading…</p>
            )}
          </SheetContent>
        )}
      </Sheet>
      {editing && candidate.data && (
        <CandidateForm
          candidate={candidate.data}
          definitions={definitions}
          role={session.me.organisation.role}
          onClose={() => setEditing(false)}
          onUploaded={() => void refresh()}
          onSaved={async () => {
            setEditing(false);
            await refresh();
          }}
        />
      )}
    </>
  );
}
